import { useNavigate } from "react-router-dom";
import { useState } from "react";
import useFetch from "@/lib/useFetch";
import { PageHeader, Loading, ErrorState, KpiCard } from "@/components/common";
import { brl, pct, num, stageColor } from "@/lib/format";
import { TrendingUp, Target, CheckCircle2, Percent, AlertTriangle } from "@/components/icons";

export default function Previsao() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useFetch("/previsao");
  const [showSem, setShowSem] = useState(false);
  if (loading) return <Loading />;
  if (error) return <ErrorState onRetry={reload} />;

  const cobertura = Math.min(100, data.cobertura_meta || 0);

  return (
    <div>
      <PageHeader title="Previsão de vendas" subtitle={data.mes_label} />

      {data.vencidas_count > 0 && (
        <div
          data-testid="previsao-vencidas"
          className="mb-4 rounded-xl border border-bad/40 bg-bad-soft text-bad px-4 py-3 text-sm"
        >
          <div className="flex items-center gap-2 font-semibold mb-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            Data prevista vencida ({num(data.vencidas_count)})
          </div>
          <div className="space-y-1.5">
            {data.vencidas.map((d) => {
              const titulo = (d.titulo || "").trim();
              const cliente = (d.cliente_nome || "").trim();
              const mostrarCliente = cliente && cliente !== titulo;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => navigate(`/deal/${d.id}`)}
                  data-testid={`previsao-vencida-${d.id}`}
                  className="w-full text-left bg-white/70 hover:bg-white rounded-lg px-3 py-2 flex items-center justify-between gap-2 border border-bad/20"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-bad truncate">{titulo || cliente || "—"}</div>
                    <div className="text-xs text-bad/80 truncate">
                      {mostrarCliente ? `${cliente} · ` : ""}{d.owner_name} · {d.stage} · vencida há {d.dias_vencida}d
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-bad shrink-0">{brl(d.valor)}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {data.sem_previsao_count > 0 && (
        <div
          data-testid="previsao-sem-data-alert"
          className="mb-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 text-sm"
        >
          <button
            type="button"
            onClick={() => setShowSem((v) => !v)}
            data-testid="previsao-sem-toggle"
            className="w-full text-left flex items-start gap-2"
          >
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div className="flex-1">
              <b>{num(data.sem_previsao_count)} {data.sem_previsao_count === 1 ? "oportunidade aberta" : "oportunidades abertas"}</b>{" "}
              sem data prevista ou sem valor — {data.sem_previsao_count === 1 ? "não entra" : "não entram"} nesta soma. Toque para ver a lista.
            </div>
          </button>
          {showSem && (
            <div className="mt-3 space-y-1.5" data-testid="previsao-sem-lista">
              {(data.sem_previsao_items || []).map((d) => {
                const titulo = (d.titulo || "").trim();
                const cliente = (d.cliente_nome || "").trim();
                const mostrarCliente = cliente && cliente !== titulo;
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => navigate(`/deal/${d.id}`)}
                    className="w-full text-left bg-white/80 hover:bg-white rounded-lg px-3 py-2 flex items-center justify-between gap-2 border border-amber-200"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-amber-900 truncate">{titulo || cliente || "—"}</div>
                      <div className="text-xs text-amber-900/70 truncate">
                        {mostrarCliente ? `${cliente} · ` : ""}{d.owner_name} · {d.stage} · {d.motivo === "sem_valor" ? "sem valor" : "sem data prevista"}
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-amber-900 shrink-0">{brl(d.valor || 0)}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        <KpiCard label="Previsão ponderada" value={brl(data.ponderado)} sub={`${num(data.count)} ${data.count === 1 ? "oportunidade" : "oportunidades"}`} icon={TrendingUp} tone="neutral" testId="prev-ponderado" help="Soma de valor × probabilidade/100 das oportunidades abertas no período (insights.previsao → ponderado)." />
        <KpiCard label="Comprometido" value={brl(data.comprometido)} sub="prob. ≥ 80%" icon={CheckCircle2} tone="neutral" help="Soma do valor das oportunidades abertas com probabilidade maior ou igual a 80% (insights.previsao)." />
        <KpiCard label="Provável" value={brl(data.provavel)} sub="prob. 50–79%" icon={Percent} tone="neutral" help="Soma do valor das oportunidades abertas com probabilidade entre 50% e 79% (insights.previsao)." />
        {data.sem_cota_real ? (
          <KpiCard
            label="Cota real"
            value="Não cadastrada"
            sub={data.meta_equipe_interna ? `Meta interna da equipe: ${brl(data.meta_equipe_interna)}` : "Lance a cota em Equipe → Lançar cotas"}
            icon={Target}
            tone="neutral"
            testId="prev-meta"
            help="Enquanto a cota real do período não é lançada, não mostramos % nem falta. A soma das cotas dos vendedores aparece abaixo como 'Meta interna da equipe'."
          />
        ) : (
          <KpiCard label={data.cota_label || "Meta"} value={brl(data.meta)} sub={`falta ${brl(data.falta_para_meta)}`} icon={Target} tone="neutral" testId="prev-meta" help="Cota real do responsável no mês (ou soma da equipe para vendedor) menos o já realizado." />
        )}
      </div>

      <div className="bg-card rounded-2xl border border-black/5 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)] mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-navy">Cobertura da meta</span>
          <span className="text-sm font-semibold text-brand">{pct(cobertura, 1)}</span>
        </div>
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-brand rounded-full transition-all duration-700" style={{ width: `${cobertura}%` }} />
        </div>
        <p className="text-xs text-muted-foreground mt-2">Realizado ({brl(data.realizado)}) + previsão ponderada sobre a meta do período.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {data.por_vendedor?.length > 0 && (
          <div className="bg-card rounded-2xl border border-black/5 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">Por responsável</h3>
            <div className="space-y-3">
              {data.por_vendedor.map((v) => (
                <div key={v.owner_id} className="flex items-center justify-between">
                  <div className="text-sm text-navy">{v.owner_name}<span className="text-xs text-muted-foreground ml-2">{v.count} op.</span></div>
                  <div className="text-sm font-semibold text-navy">{brl(v.ponderado)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {data.por_etapa?.length > 0 && (
          <div className="bg-card rounded-2xl border border-black/5 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">Por estágio</h3>
            <div className="space-y-3">
              {data.por_etapa.map((e) => (
                <div key={e.stage} className="flex items-center justify-between">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${stageColor(e.stage)}`}>{e.stage}</span>
                  <div className="text-sm font-semibold text-navy">{brl(e.ponderado)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {data.items?.length > 0 && (
        <div className="mt-6">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Oportunidades previstas</h3>
          <div className="space-y-2">
            {data.items.map((d) => {
              const titulo = (d.titulo || "").trim();
              const cliente = (d.cliente_nome || "").trim();
              const mostrarCliente = cliente && cliente !== titulo;
              return (
                <div key={d.id} onClick={() => navigate(`/deal/${d.id}`)} className="bg-card rounded-xl border border-black/5 p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors">
                  <div className="min-w-0"><div className="font-medium text-navy truncate">{titulo || cliente || "—"}</div><div className="text-xs text-muted-foreground truncate">{mostrarCliente ? `${cliente} · ` : ""}{d.prob}%</div></div>
                  <div className="text-sm font-semibold text-navy shrink-0">{brl(d.valor)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
